<template>
  <ContextColorProvider class="portability" context="trade">
    <NavBar sticky>
      <template #left>
        <BackButton />
      </template>
    </NavBar>
    <main v-if="isLoading" class="portability__main">
      <PortabilityRequestsLoader :size="32" />
    </main>
    <main v-else-if="hasError" class="portability__helper-wrapper">
      <HelperViewStatus
        class="portability__helper"
        title="Houve um problema ao carregar a solicitação"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="fetchPortabilityRequestsData"
      />
    </main>
    <main
      v-else-if="!hasPortabilityRequests"
      class="portability__helper-wrapper"
    >
      <HelperViewContextual
        class="portability__helper"
        icon="ED0030"
        description="Você não possui solicitações para avaliação"
      />
    </main>
    <main v-else class="portability__main">
      <HeadingDisplay
        class="portability__heading"
        title="Portabilidade"
        head="Suas solicitações"
        size="medium"
      />
      <section>
        <InfiniteScroll
          class="instrument-modal__results"
          :max-per-page="itemsPerPage"
          :request="handleFetchMoreRequests"
          :item-list="portabilityRequests"
          :initial-page="1"
          not-found-items-message="Nenhuma solicitação encontrada"
          whitelabel
        >
          <template #default="portability">
            <PortabilityCard
              :portability="portability"
              class="portability__card"
            />
          </template>
        </InfiniteScroll>
      </section>
    </main>
  </ContextColorProvider>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';

import {
  ContextColorProvider,
  NavBar,
  HeadingDisplay,
  HelperViewStatus,
  HelperViewContextual,
  InfiniteScroll
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons';
import { PortabilityCard } from './components/PortabilityCard';
import { PortabilityRequestsLoader } from './components/PortabilityRequestsLoader';
import {
  getPortabilityRequests,
  PortabilityRequest
} from '@/modules/trade/services/portability';

const isLoading = ref(true);
const hasError = ref(false);
const page = ref(1);
const itemsPerPage = ref(10);
const hasMorePages = ref(false);
const portabilityRequests = ref<PortabilityRequest[] | null>(null);

const hasPortabilityRequests = computed(() => {
  return portabilityRequests.value && portabilityRequests.value.length > 0;
});

const handleFetchMoreRequests = async () => {
  if (hasMorePages.value) {
    page.value++;
    await fetchPortabilityRequestsData();
  }
};

const fetchPortabilityRequestsData = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;

    const response = await getPortabilityRequests(
      itemsPerPage.value,
      page.value
    );

    portabilityRequests.value = [
      ...(portabilityRequests.value || []),
      ...response.items
    ];
    page.value = response.pagination.page;
    hasMorePages.value = response.pagination.hasMore;
  } catch (e) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await fetchPortabilityRequestsData();
});
</script>
<style lang="less" scoped>
.portability {
  display: flex;
  min-height: 100vh;
  flex-direction: column;

  &__main {
    padding: @size-spacing-x1000 @size-spacing-x500 @size-spacing-x600
      @size-spacing-x500;
    max-width: 592px;
    width: 100%;
    margin: 0 auto;
    box-sizing: content-box;

    .to-lg({ padding: @size-spacing-x400 @size-spacing-x400;});
  }

  &__heading {
    margin-bottom: @size-spacing-x800;
  }

  &__card:not(:last-child) {
    margin-bottom: @size-spacing-x500;
  }

  &__helper-wrapper {
    flex-grow: 1;
    display: flex;
  }

  &__helper {
    display: block;
    margin: 0 auto;
    align-self: center;
  }
}
</style>
